<template>
    <div class="pr fns" :class="[is_open ? 'el--active' : 'el--inactive']">
        <template v-if="uiBtnDisplay === 'pill'">
            <ButtonPill ref="trigger"
                class="el--dropdown-pill"
                :icon="uiBtnIcon"
                :class="['el--t-' + uiBtnType, 'el--s-' + uiBtnSize, uiBtnClass]"
                :disabled="!uiAdjustable"
                :qa="qaButtonTag"
                @click.prevent.stop="toggle" />
        </template>
        <template v-else-if="uiBtnDisplay === 'icon'">
            <ButtonIcon ref="trigger"
                class="el--dropdown-icon"
                :icon="uiBtnIcon"
                :html="uiBtnHtml"
                :class="['el--t-' + uiBtnType, 'el--s-' + uiBtnSize, uiBtnClass]"
                :disabled="!uiAdjustable"
                :qa="qaButtonTag"
                @click.prevent.stop="toggle" />
        </template>
        <template v-else-if="uiBtnDisplay === 'select'">
            <button ref="trigger"
                type="button"
                class="f fh fa-c el--dropdown-select dw-10"
                :class="['el--t-' + uiBtnType, 'el--s-' + uiBtnSize, uiBtnClass]"
                :disabled="!uiAdjustable"
                :qa="qaButtonTag"
                @click.prevent.stop="toggle">
                <slot name="btn-content"></slot>
                <span class="fg"></span>
                <i class="sm-l-s sm-r-xs lnr-chevron-down"></i>
            </button>
        </template>
        <template v-else-if="uiBtnDisplay === 'custom'">
            <button ref="trigger"
                class="el--dropdown-custom"
                type="button"
                :class="['el--t-' + uiBtnType, 'el--s-' + uiBtnSize, uiBtnClass]"
                :disabled="!uiAdjustable"
                :qa="qaButtonTag"
                @click.prevent.stop="toggle">
                <slot name="btn-content"></slot>
            </button>
        </template>
        <ul v-if="is_open"
            class="pa el-dropdown"
            :class="['el--a-' + align, 'el--va-' + valign, uiIsComponent ? 'el--t-custom' : '']"
            :style="dropdownStyle"
            v-mead-move="uiMoveable">
            <slot></slot>
        </ul>
    </div>
</template>

<script>
import { guuid } from '@aretheyhappy/utils';
import ButtonPill from '../atoms/ButtonPill.vue';
import ButtonIcon from '../atoms/ButtonIcon.vue';

export default {
    name: 'mead-dropdown',
    components: {ButtonPill, ButtonIcon},
    data () {
        return {
            uid                 : guuid(),
            is_open             : false,
            original_overflow   : false,
        };
    },
    props: {
        /**
         * Unique key identifying this dropdown instance on the shared Flash bus. Used to
         * open/close this specific dropdown from elsewhere and to tell other dropdowns
         * sharing the bus to close themselves when this one opens.
         */
        pubkey          : {type: [String, Boolean], default: () => false},
        /**
         * Horizontal alignment of the dropdown menu relative to its trigger button.
         * Accepts 'r' (right) or 'l' (left).
         */
        align           : {type: String, default: () => 'r'},
        /**
         * Vertical alignment of the dropdown menu relative to its trigger button.
         * Accepts 'b' (bottom) or 't' (top).
         */
        valign          : {type: String, default: () => 'b'},
        /**
         * Which trigger button style to render: 'pill', 'icon', 'select', or 'custom'.
         * Determines which trigger markup is used to open the dropdown.
         */
        uiBtnDisplay    : {type: String, default: () => 'pill'},
        /**
         * Icon name passed through to the pill/icon trigger button.
         */
        uiBtnIcon       : {type: [Boolean, String], default: () => 'ellipsis'},
        /**
         * Raw HTML content passed through to the icon trigger button, used instead
         * of (or alongside) an icon name.
         */
        uiBtnHtml       : {type: [Boolean, String], default: () => false},
        /**
         * Visual type/style modifier applied to the trigger element, e.g. 'default'.
         */
        uiBtnType       : {type: String, default: () => 'default'},
        /**
         * Size modifier applied to the trigger element, e.g. 's' for small.
         */
        uiBtnSize       : {type: String, default: () => 's'},
        /**
         * Additional CSS class(es) applied to the trigger element.
         */
        uiBtnClass      : {type: String, default: () => ''},
        /**
         * Whether the dropdown menu's contents are a custom component rather than the
         * standard list items, used to switch the menu's styling to the custom type.
         */
        uiIsComponent   : {type: Boolean, default: () => false},
        /**
         * Whether the dropdown trigger is enabled/interactive. When false, the
         * trigger button is disabled and the dropdown cannot be opened.
         */
        uiAdjustable    : {type: Boolean, default: () => true},
        /**
         * Whether the open dropdown menu can be freely moved/dragged around the
         * screen via the move directive.
         */
        uiMoveable      : {type: Boolean, default: () => false},
        /**
         * Additional inline style object merged into the dropdown menu container's style.
         */
        uiContainerStyle: {type: Object, default: () => ({})},
        /**
         * Maximum height (in rem) for the dropdown menu before it becomes scrollable.
         * When false, no max height is applied.
         */
        uiMaxHeightRem  : {type: [Boolean, Number], default: () => false},
        /**
         * QA tag applied to the trigger element for automated test targeting.
         */
        qaButtonTag     : {type: String, default: ''},
    },
    methods: {
        documentClickHandler: function (evt) {
            if (this.$el.contains(evt.target)) {
                return;
            }
            this.close();
        },
        close: function () {
            this.is_open = false;
            document.removeEventListener('click', this.documentClickHandler);
            this.$emit('dropdownClose');
        },
        open: function () {
            // Close other dropdowns on the page
            MeadCommon.Flash.publish('dropdown-close-other', this.pubkey);
            this.is_open = true;
            document.addEventListener('click', this.documentClickHandler);
            this.$emit('dropdownOpen');
        },
        closeFromOther: function (uid) {
            if (this.pubkey === uid || this.is_open === false) return;
            this.close();
        },
        toggle: function () {
            if (this.is_open) {
                this.close();
            } else {
                this.open();
            }
        },
    },
    computed: {
        dropdownStyle: function () {
            if (!this.uiMaxHeightRem) return Object.assign({}, this.uiContainerStyle);
            return Object.assign({
                overflowY: 'auto',
                maxHeight: `${this.uiMaxHeightRem}rem`,
            }, this.uiContainerStyle);
        },
    },
    created () {
        MeadCommon.Flash.subscribe(this.uid, `${this.pubkey}:open`, this.open);
        MeadCommon.Flash.subscribe(this.uid, `${this.pubkey}:close`, this.close);
        MeadCommon.Flash.subscribe(this.uid, 'dropdown-close-other', this.closeFromOther);
    },
    destroyed () {
        MeadCommon.Flash.unsubscribe(this.uid);
        document.removeEventListener('click', this.documentClickHandler);
    },
};
</script>

<style lang="scss" scoped>
@use '../../../sass/variables' as *;

.el--dropdown-select {
    background: var(--c-brand-light-bg);
    color: var(--c-brand-light-fg);
    border: none;
    border-radius: $br-m;
    padding: 0 $s-s;
    font-size: $fs-m;
    font-weight: 400;
    font-family: $ff-b;
    cursor: pointer;

    &.el--s-s {
        height: 3rem;
        line-height: 1;
        font-size: $fs-s;
    }

    &.el--s-m {
        height: 4rem;
        line-height: 1;
    }

    &:hover,
    &:active {
        outline: none;
        background: var(--c-brand-default-bg);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: .5;
    }
}

.el-dropdown {
    padding: $s-s;
    z-index: $zx-nav-primary + 1;
    top: calc(100% - .5rem);
    border-radius: $br-m;
    background: var(--c-panel-bg);
    font-family: $ff-b;
    font-size: $fs-s;
    user-select: none;
    box-shadow: var(--c-shade-l);
    border: 1px solid var(--c-panel-border);

    &:not(.el--t-custom) {
        max-width: 40rem;
        min-width: calc(100% + 1rem);

        li {
            padding: $s-xs;
            display: flex;
            word-break: break-word;
            white-space: nowrap;
            cursor: pointer;
            line-height: $lh-s;
            font-size: $fs-s;
            flex-shrink: 0;
            letter-spacing: $ls-s;
            text-align: left;
            border-radius: $br-s;

            &:not(.fa-l):not(.fa-r) {
                align-items: center;
            }

            &:not(.fj-c):not(.fj-r) {
                justify-content: left;
            }

            &.el-dropdown-separator {
                cursor: default;
                border-top: 1px solid var(--c-panel-border);
                padding: 0;
                margin: $s-s 0;
            }

            span,
            small {
                overflow: hidden;
                text-overflow: ellipsis;
                word-wrap: break-word;
            }

            i {
                font-size: $fs-xl;
                display: inline-block;
                margin-right: $s-s;
                width: 2rem;
            }

            //  States

            &:not(.upgrade-required):not(.el--active):not(.el--noninteractive):not(.el-dropdown-separator):active,
            &:not(.upgrade-required):not(.el--active):not(.el--noninteractive):not(.el-dropdown-separator):hover {
                background-color: var(--c-brand-default-bg);

                &,
                & .te-subnote {
                    color: var(--c-brand-default-fg);
                }
            }

            //  Modifiers

            &.el--active {
                background-color: var(--c-brand-active-bg);

                &,
                & .te-subnote {
                    color: var(--c-brand-active-fg);
                }
            }
        }
    }

    //
    //  Modifiers
    //

    //  Horizontal alignment
    &.el--a-r {
        right: .5rem;
    }

    &.el--a-l {
        left: .5rem;
    }

    //  Vertical alignment
    &,
    &.el--va-b {
        bottom: initial;
        top: calc(100% - .5rem);
    }

    &.el--va-t {
        bottom: calc(100% - .5rem);
        top: initial;
    }

    //  Types
    &.el--t-chromeless {
        padding: 0;
        border-radius: 0;
        background: transparent;
        box-shadow: none;
        border-width: 0;
        min-width: initial;
        max-width: initial;
    }
}

</style>
